import React from 'react';
import { Loader2, CheckCircle, X, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { useBackgroundTasks } from '../lib/BackgroundTaskContext';

export const BackgroundTaskBar: React.FC = () => {
  const { tasks, dismissTask } = useBackgroundTasks();

  if (tasks.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      maxWidth: 380,
      width: '100%',
    }}>
      {tasks.map(task => {
        const pct = task.total > 0 ? Math.round((task.current / task.total) * 100) : 0;
        const isRunning = task.status === 'running';
        const isDone = task.status === 'done';

        return (
          <div
            key={task.id}
            style={{
              background: 'var(--surface, #fff)',
              border: `1px solid ${isDone ? 'var(--success, #22c55e)' : 'var(--border, #e5e7eb)'}`,
              borderRadius: 12,
              padding: '12px 16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              animation: 'slideUp 0.3s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {isRunning ? (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--primary)' }} />
              ) : isDone ? (
                <CheckCircle size={16} style={{ color: 'var(--success, #22c55e)' }} />
              ) : (
                <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <ImageIcon size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                  {task.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {isRunning
                    ? `${task.current} / ${task.total} Bilder${task.errors > 0 ? ` (${task.errors} Fehler)` : ''}`
                    : isDone
                      ? `${task.total} Bilder fertig${task.errors > 0 ? ` (${task.errors} Fehler)` : ''} ✅`
                      : `Abgebrochen`
                  }
                </div>
              </div>
              <button
                onClick={() => dismissTask(task.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 6,
                  color: 'var(--text-muted)',
                  display: 'flex',
                }}
                title={isRunning ? 'Abbrechen' : 'Schließen'}
              >
                <X size={14} />
              </button>
            </div>

            {/* Progress bar */}
            <div style={{
              width: '100%',
              height: 4,
              background: 'var(--border, #e5e7eb)',
              borderRadius: 4,
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: isDone
                  ? 'var(--success, #22c55e)'
                  : 'var(--primary, #E03705)',
                borderRadius: 4,
                transition: 'width 0.5s ease-out',
              }} />
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
